import { Contractor } from "@bb/game-products-ui";
import { createSelector, Selector } from "reselect";

import filter from "lodash/filter";
import map from "lodash/map";

import { BetParam, BetCreateParams } from "src/store/api";
import { Bet, RootState } from "src/types";
import { groupBetCreateByBalanceId } from "src/utils/groupBetCreateByBalanceId";

import { getBets, getNextId } from "./coreSelectors";
import { getContractor, getToken } from "./userSelectors";

/**
 * Селектор получения ставок для заключения пари с учетом gameId и статуса
 */
const getBetsByGameIdAndsStatus = createSelector<
  [Selector<RootState, Array<Bet>>, Selector<RootState, number>],
  Array<Bet>
>([getBets, getNextId], (bets, nextId) =>
  filter(bets, { gameId: nextId, status: "selected" })
);

/**
 * Селектор формирования ставок для заключения пари
 */
const getBetParams = createSelector<
  [Selector<RootState, Array<Bet>>],
  Array<BetParam>
>([getBetsByGameIdAndsStatus], (bets) =>
  map(bets, (bet) => {
    const betParam: BetParam = {
      amount: bet.amount,
      balanceId: bet.balanceId,
      rate: bet.rate,
      type: bet.type,
    };

    switch (bet.type) {
      case "sumtop2":
      case "sumtop3":
      case "top1":
      case "top2":
      case "top3": {
        const [first] = bet.racers;
        if (first) {
          betParam["line"] = first;
        }
        break;
      }
      case "competition": {
        const [first, second] = bet.racers;
        if (first && second) {
          betParam["line"] = first;
          betParam["second"] = second;
        }
        break;
      }
      case "personal": {
        const [first] = bet.racers;
        const [place] = bet.places;
        if (first && place) {
          betParam["line"] = first;
          betParam["second"] = place;
        }
        break;
      }
      default:
        break;
    }

    return betParam;
  })
);

/**
 * Селектор получения аргументов для заключения пари
 */
export const getBetCreateQueryArgs = createSelector<
  [
    Selector<RootState, Array<Bet>>,
    Selector<RootState, Array<BetParam>>,
    Selector<RootState, Contractor>,
    Selector<RootState, number>,
    Selector<RootState, string>
  ],
  BetCreateParams
>(
  [getBetsByGameIdAndsStatus, getBetParams, getContractor, getNextId, getToken],
  (bets, betParams, contractor, gameId, token) => ({
    bets,
    betParams,
    contractor,
    gameId,
    token,
  })
);

/**
 * Селектор аргументов bet.create — отдельный запрос на каждый тип баланса.
 */
export const getBetCreateQueryArgsSplit = createSelector(
  [getBetCreateQueryArgs],
  (args) => groupBetCreateByBalanceId(args)
);
